import { FC, useRef, useState } from "react";
import { Attack, PatientDetails } from "../types";
import {
  Button,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  SubTitle,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { ALL_GRAPH_NAMES, Graph, toGraphName } from "../types";
import { collateGraphData, toGraphLabel } from "../helpers";
import { FileDownload } from "@mui/icons-material";
import { saveAs } from "file-saver";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import { blue } from "@mui/material/colors";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  SubTitle,
  Tooltip,
  Legend,
);

// Custom plugin to change background color so that we can export the graph with
// a white background
const plugin = {
  id: "customCanvasBackgroundColor",
  beforeDraw: (chart: any, args: any, options: any) => {
    const { ctx } = chart;
    ctx.save();
    ctx.globalCompositeOperation = "destination-over";
    ctx.fillStyle = options.color || "#99ffff";
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  },
};

interface PatientHistoryTabProps {
  patientDetails: PatientDetails;
  attacks: Attack[];
  attacksLoaded: boolean;
}

const PatientStatisticsTab: FC<PatientHistoryTabProps> = ({
  patientDetails,
  attacks,
  attacksLoaded,
}) => {
  const [graph, setGraph] = useState<Graph>("headachesPerMonth");
  const patientGraphRef = useRef<ChartJSOrUndefined<
    "bar",
    number[],
    string
  > | null>(null);

  const handleDownload = () => {
    const base64Image = patientGraphRef.current?.canvas.toDataURL("image/png")!;
    saveAs(base64Image, "graph.png");
  };

  const options = {
    responsive: true,
    scales: {
      y: {
        ticks: {
          // rounds step size to 0 decimal places - this means no decimal values
          // on Y axis at low number of headaches, but also doesn't force a step
          // size of 1 for large numbers
          precision: 0,
        },
      },
    },
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: toGraphName(graph),
        font: {
          size: 18,
        },
      },
      subtitle: {
        display: true,
        text: `${patientDetails.firstName} ${patientDetails.lastName}`,
        font: {
          size: 18,
        },
        padding: {
          bottom: 30,
        },
      },
      customCanvasBackgroundColor: {
        color: "white",
      },
    },
  };

  const collatedByMonth = collateGraphData(graph, attacks);

  const data = {
    labels: Array.from(collatedByMonth.keys()),
    datasets: [
      {
        label: toGraphLabel(graph),
        data: Array.from(collatedByMonth.values()),
        backgroundColor: blue[100],
        // backgroundColor: "rgba(255, 99, 132, 0.5)",
      },
    ],
  };

  return !attacksLoaded ? (
    <LinearProgress />
  ) : (
    <Stack spacing={2}>
      <FormControl>
        <InputLabel id="patient-graph-label">Graph</InputLabel>
        <Select
          labelId="patient-graph-label"
          label="graph"
          value={graph}
          onChange={(e) => setGraph(e.target.value as Graph)}
        >
          {ALL_GRAPH_NAMES.map((graph) => (
            <MenuItem key={graph} value={graph}>
              {toGraphName(graph)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Bar
        ref={patientGraphRef}
        options={options}
        data={data}
        plugins={[plugin]}
      />
      <Button
        variant="contained"
        startIcon={<FileDownload />}
        onClick={handleDownload}
      >
        Download graph
      </Button>
    </Stack>
  );
};

export default PatientStatisticsTab;
